import { useEffect, useState } from 'react';
import { Alert, Box, Spinner, Typography } from '@glplus/glpweb-dsl';
import { adminApiClient, type OrderLocationsResponse } from '../lib/adminApiClient';
import { Pill } from './Pill';

/**
 * Where one order was actually shopped — the audit detail, expanded in place.
 *
 * 🔒 Rod, 2026-09-13: *"we need to know which location every order was picked up
 * at for our internal auditing and reporting."* An order can touch several
 * stores, and a replacement can be picked up somewhere the order never named.
 *
 * 🛑 READ-ONLY, like the endpoint behind it. An audit surface that can edit the
 * thing it audits is not an audit surface.
 *
 * 🪤 Loads only when opened. Every row on the parent list would otherwise fire
 * its own request for a detail nobody had asked to see.
 */
const money = (c: number | null) => (c === null ? '—' : `$${(c / 100).toFixed(2)}`);

const STATUS_PILL: Record<string, { fg: string; bg: string }> = {
  planned: { fg: '#5f6b7a', bg: '#f1f5f9' },
  shopping: { fg: '#7A4A08', bg: '#FDF3E1' },
  picked_up: { fg: '#15803d', bg: '#dcfce7' },
  skipped: { fg: '#b42318', bg: '#fee4e2' },
};

export function OrderLocationsPanel({ orderId }: { orderId: string }) {
  const [data, setData] = useState<OrderLocationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    adminApiClient
      .getOrderLocations(orderId)
      .then((d) => { if (live) setData(d); })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Could not load the pickup locations'); });
    return () => { live = false; };
  }, [orderId]);

  if (error) return <Box sx={{ mt: 1 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return <Box sx={{ mt: 1 }}><Spinner color="brand" /></Box>;

  const { locations, summary } = data;

  return (
    <Box sx={{ mt: 1.5, p: 1.75, backgroundColor: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
        WHERE THIS ORDER WAS SHOPPED
      </Typography>

      {locations.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
          No pickup locations recorded. Orders placed before 2026-09-13 predate this table.
        </Typography>
      ) : (
        <>
          {/*
            🛑 The one number that should stop an auditor, stated before the rows
            rather than left to be spotted in them.
          */}
          {summary.receiptsMissing > 0 && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning">
                {summary.receiptsMissing === 1
                  ? 'One location was picked up with no receipt recorded.'
                  : `${summary.receiptsMissing} locations were picked up with no receipt recorded.`}
              </Alert>
            </Box>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 1 }}>
            {summary.total} {summary.total === 1 ? 'location' : 'locations'}
            {summary.unplanned > 0 ? ` · ${summary.unplanned} unplanned` : ''}
            {' · '}{summary.pickedUp} picked up · {money(summary.registerTotalCents)} at the register
          </Typography>

          <Box sx={{ display: 'grid', gap: 1 }}>
            {locations.map((l) => {
              const pill = STATUS_PILL[l.status] ?? STATUS_PILL.planned;
              return (
                <Box
                  key={l.orderLocationId}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: 2, flexWrap: 'wrap', backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderLeft: `4px solid ${l.planned ? '#e2e8f0' : '#F67A1A'}`,
                    borderRadius: 1.5, px: 1.5, py: 1,
                  }}
                >
                  <Box sx={{ minWidth: 220 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, color: '#1f2937' }}>{l.storeName ?? l.storeId}</Typography>
                      <Pill label={l.status.replace('_', ' ').toUpperCase()} fg={pill.fg} bg={pill.bg} />
                      {/* The whole reason this table exists — never let it blend in. */}
                      {!l.planned && <Pill label="UNPLANNED" fg="#7A4A08" bg="#FDF3E1" />}
                      {l.cardLive && <Pill label="CARD LIVE" fg="#b42318" bg="#fee4e2" />}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {[l.where, l.vendorName ?? l.chainName].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                    {!l.planned && (
                      <Typography variant="caption" sx={{ color: '#7A4A08' }}>
                        Something priced elsewhere was picked up here.
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'right', minWidth: 150 }}>
                    <Typography variant="body2" sx={{ color: '#1f2937' }}>
                      {money(l.registerTotalCents)}
                      {l.registerTaxCents !== null && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                          {' '}({money(l.registerTaxCents)} tax)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      expected {money(l.expectedCents)}
                    </Typography>
                    {l.receiptMissing ? (
                      <Typography variant="caption" sx={{ color: '#b42318', fontWeight: 700 }}>
                        No receipt
                      </Typography>
                    ) : l.receiptPhotoUrl ? (
                      <Typography variant="caption" sx={{ color: '#15803d' }}>Receipt on file</Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
